'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QuizQuestion {
  id: number;
  question: string;
  options: {
    text: string;
    pathway: 'individual' | 'organizer' | 'supporter' | 'advocate';
  }[];
}

interface OnboardingQuizProps {
  onComplete: (pathway: string) => void;
}

const OnboardingQuiz: React.FC<OnboardingQuizProps> = ({ onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);

  const questions: QuizQuestion[] = [
    {
      id: 1,
      question: "What brings you to the SIG Day (The Global No Enemy Festival) movement?",
      options: [
        { text: "Personal healing and inner peace", pathway: 'individual' },
        { text: "Building peace in my community", pathway: 'organizer' },
        { text: "Supporting humanitarian causes", pathway: 'supporter' },
        { text: "Advocating for systemic change", pathway: 'advocate' },
      ],
    },
    {
      id: 2,
      question: "How do you prefer to contribute?",
      options: [
        { text: "Personal transformation journey", pathway: 'individual' },
        { text: "Organizing events and gatherings", pathway: 'organizer' },
        { text: "Financial support and donations", pathway: 'supporter' },
        { text: "Policy advocacy and research", pathway: 'advocate' },
      ],
    },
    {
      id: 3,
      question: "What's your availability commitment?",
      options: [
        { text: "Daily personal practice (15-30 min)", pathway: 'individual' },
        { text: "Weekly community organizing (2-5 hours)", pathway: 'organizer' },
        { text: "Monthly financial contributions", pathway: 'supporter' },
        { text: "Ongoing advocacy campaigns", pathway: 'advocate' },
      ],
    },
  ];

  const handleAnswer = (pathway: string) => {
    const newAnswers = [...answers, pathway];
    setAnswers(newAnswers);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const pathwayCounts = newAnswers.reduce((acc, p) => {
        acc[p] = (acc[p] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const recommendedPathway = Object.entries(pathwayCounts).sort(
        ([, a], [, b]) => b - a
      )[0][0];

      onComplete(recommendedPathway);
    }
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="bg-card rounded-lg p-8 border border-border max-w-2xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-muted-foreground">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="text-sm font-medium text-primary">{Math.round(progress)}%</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <h3 className="text-2xl font-headline font-bold text-foreground mb-6">
        {questions[currentQuestion].question}
      </h3>

      <div className="space-y-3">
        {questions[currentQuestion].options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleAnswer(option.pathway)}
            className="w-full p-4 bg-background border border-border rounded-lg text-left transition-all duration-200 hover:border-primary hover:bg-primary/5 group"
          >
            <div className="flex items-center justify-between">
              <span className="text-foreground font-medium group-hover:text-primary">
                {option.text}
              </span>
              <Icon
                name="ChevronRightIcon"
                size={20}
                className="text-muted-foreground group-hover:text-primary transition-colors"
              />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default OnboardingQuiz;